import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import { UpdatedUserProfile, updateUserProfileApi, UpdateUserProfilePayload } from '../../API/UserProfileApi/UpdateUserProfile';
import { getUserProfile, UserProfile } from '../../API/UserProfileApi/GetUserProfile';
import ReactGA from "react-ga4";
import { useLocalization } from '../Context/MultiLanguageContext';

interface UpdateUserProfileProps {
  updateUserName: (newUserName: string) => void;
}

const UpdateUserProfile: React.FC<UpdateUserProfileProps> = ({ updateUserName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLocalization();

  const { userProfile } = location.state as { userProfile: UserProfile };

  const [userName, setUserName] = useState(userProfile.userName);
  const [phoneNumber, setPhoneNumber] = useState(userProfile.phoneNumber);
  const [errorMessage, setErrorMessage] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });
  }, []);
  useEffect(() => {
    setIsFormChanged(
      userName !== userProfile.userName || phoneNumber !== userProfile.phoneNumber
    );
  }, [userName, phoneNumber, userProfile]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[A-Za-z\s]*$/.test(value)) {
      setUserName(value);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[\d+]*$/.test(value)) {
      setPhoneNumber(value);
    }

  };

  const handleUpdate = async () => {
    if (!userName.trim()) {
      toast.info(t("fullNameProvide"));
      return;
    }
    if (!phoneNumber) {
      toast.info(t("fullNumberProvide"));
      return;
    }
    const phoneStr = phoneNumber.toString();
    if (!/^\+?\d{10,18}$/.test(phoneStr)) {
      toast.info(t("validNumberProvide"));
      return;
    }



    const token = localStorage.getItem("token");
    if (!token) {
      toast.error(t("sessionExpired"));
      navigate('/');
      return;
    }

    setIsSubmitting(true);

    const payload: UpdateUserProfilePayload = {
      userName,
      phoneNumber: phoneStr,
      azureToken: userProfile.azureToken,
      isRegisteredUser: true,
    };

    ReactGA.event({
      category: "UpdateProfile",
      action: "Update Attempted",
      label: userName,
    });

    try {
      await updateUserProfileApi(token, payload, t);
      const refreshedProfile = await getUserProfile(t);

      if (refreshedProfile.userName) {
        updateUserName(refreshedProfile.userName);
        localStorage.setItem("userName", refreshedProfile.userName);
        toast.success(t("profileUpdated"));

        ReactGA.event({
          category: "UpdateProfile",
          action: "Profile Updated",
          label: refreshedProfile.userName,
        });
        navigate("/home");
      } else {
        toast.error(t("invalidProfileData"));
      }
    } catch (error) {
      // console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };



  const fetchUserProfile = async () => {
    try {
      const data = await getUserProfile(t);
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      // console.error("Error fetching profile:", error);
      toast.error(t("profileDataloaderror"));
    }
  };


  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('authToken');
localStorage.removeItem('token');
    localStorage.removeItem('userName');


    sessionStorage.clear();
    ReactGA.event({
      category: "UpdateProfile",
      action: "Logout",
      label: userName,
    });
    navigate("/", { replace: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUpdate();
    }
  };


  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header
        userName={userName}
        fetchUserProfile={fetchUserProfile}
        handleLogout={handleLogout}
      />
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-6">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">
            {t("updateUserProfile")}
          </h2>
          <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md">
            {errorMessage && <p className="text-red-500 mb-4">{errorMessage}</p>}

            <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("FullName")}
                </label>
                <input
                  id="userName"
                  type="text"
                  maxLength={15}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={userName}
                  onChange={handleNameChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter letters only"
                />
              </div>

              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("phoneNumber")}
                </label>
                <input
                  id="phoneNumber"
                  type="text"
                  maxLength={15}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter numbers only"
                />
              </div>

              <div className="col-span-1 md:col-span-2 flex flex-col sm:flex-row items-center justify-end gap-4 mt-4">
                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={!isFormChanged || isSubmitting}
                  className={`py-2 px-4 text-white rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${isFormChanged && !isSubmitting
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-400 cursor-not-allowed'}`}
                >
                  {isSubmitting ? t("updating") : t("update")}
                </button>
                <button
                  onClick={() => navigate('/home')}
                  className="py-2 px-4 bg-gray-400 text-white rounded-md shadow hover:bg-gray-500 focus:outline-none"
                  disabled={isSubmitting}
                >
                  {t("cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
      <Footer />
      <ToastContainer />
    </div>
  );
};

export default UpdateUserProfile;
